import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";

// Local storage keys
const VISITOR_KEY = "bdu_visitor_id";
const SESSION_KEY = "bdu_session_id";
const SESSION_TS_KEY = "bdu_session_ts";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes of inactivity ends a session

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

function uuid() {
  return "vx-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function getSessionId() {
  const now = Date.now();
  const last = parseInt(localStorage.getItem(SESSION_TS_KEY) || "0", 10);
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid || now - last > SESSION_TIMEOUT_MS) {
    sid = uuid();
    localStorage.setItem(SESSION_KEY, sid);
  }
  localStorage.setItem(SESSION_TS_KEY, String(now));
  return sid;
}

// Load GA4 snippet once (only if measurement id is set)
let gaLoaded = false;
function ensureGA() {
  if (!GA_ID || gaLoaded || typeof window === "undefined") return;
  gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view=false because we handle SPA route changes manually
  window.gtag("config", GA_ID, { send_page_view: false });
}

function sendGaPageview(path) {
  if (!GA_ID || typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
}

/**
 * Silent page-view tracker. Fires POST /api/analytics/track (native) and GA4
 * page_view on every route change. Failures are ignored — analytics should
 * never break the app.
 */
export default function AnalyticsTracker() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => { ensureGA(); }, []);

  useEffect(() => {
    const path = location.pathname + (location.search || "");
    if (lastPath.current === path) return;
    lastPath.current = path;

    // Skip admin pages (noise)
    if (path.startsWith("/admin")) return;

    // Native tracker
    const payload = {
      path,
      referrer: document.referrer || null,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
    };
    api.post("/analytics/track", payload).catch(() => {});

    // GA4 page_view
    sendGaPageview(path);
  }, [location.pathname, location.search]);

  return null;
}
